import { useEffect, useId, useRef, type ReactNode } from "react";
import { Button } from "./Button";

/**
 * A modal on the native <dialog>: showModal() gives the focus trap, Esc
 * and inertness of the page behind for free, and focus returns to the
 * opener when it closes. Labelled by its title; the first field or
 * button inside takes focus.
 */
export function Dialog({ title, children, onClose, closeLabel = "Cancel" }: { title: string; children: ReactNode; onClose: () => void; closeLabel?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.open) el.showModal?.();
    const first = el.querySelector<HTMLElement>("input, select, textarea, button:not([data-dialog-close])");
    first?.focus();
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, [onClose]);
  return (
    <dialog ref={ref} aria-labelledby={titleId} className="m-auto w-[min(28rem,calc(100vw-2rem))] rounded-xl border border-line bg-surface p-5 text-ink shadow-xl backdrop:bg-black/40">
      <h2 id={titleId} className="text-lg font-semibold">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
      <div className="mt-4 flex justify-end">
        <Button data-dialog-close onClick={onClose}>
          {closeLabel}
        </Button>
      </div>
    </dialog>
  );
}
